import { useEffect, useState } from "react";

// Global state to track KaTeX loading
let katexLoadingPromise: Promise<void> | null = null;
let katexLoaded = false;

// Helper function to load KaTeX only once
const loadKaTeX = (): Promise<void> => {
  // Return existing promise if already loading
  if (katexLoadingPromise) {
    return katexLoadingPromise;
  }

  // Return resolved promise if already loaded
  if (katexLoaded || window.katex) {
    katexLoaded = true;
    return Promise.resolve();
  }

  // Create new loading promise
  katexLoadingPromise = new Promise((resolve, reject) => {
    // Check if CSS is already loaded
    const existingCSS = document.querySelector('link[href*="katex.min.css"]');
    if (!existingCSS) {
      const katexCSS = document.createElement('link');
      katexCSS.rel = 'stylesheet';
      katexCSS.href = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.css';
      document.head.appendChild(katexCSS);
    }

    // Check if JS is already loaded
    const existingScript = document.querySelector('script[src*="katex.min.js"]');
    if (!existingScript) {
      const katexScript = document.createElement('script');
      katexScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/KaTeX/0.16.9/katex.min.js';
      katexScript.onload = () => {
        katexLoaded = true;
        resolve();
      };
      katexScript.onerror = () => {
        katexLoadingPromise = null; // Reset so it can be retried
        reject(new Error('Failed to load KaTeX library'));
      };
      document.head.appendChild(katexScript);
    } else {
      // Script exists, check if katex is available
      if (window.katex) {
        katexLoaded = true;
        resolve();
      } else {
        // Script is loading, wait for it
        const checkKatex = () => {
          if (window.katex) {
            katexLoaded = true;
            resolve();
          } else {
            setTimeout(checkKatex, 50);
          }
        };
        checkKatex();
      }
    }
  });

  return katexLoadingPromise;
};

// Formula renderer component with lazy loading
export const FormulaRenderer: React.FC<{ 
  formula: string; 
  displayMode?: boolean; // true for block formulas [ ... ], false for inline ( ... )
  className?: string;
}> = ({ formula, displayMode = false, className = '' }) => {
  const [rendered, setRendered] = useState<string>('');
  const [katexReady, setKatexReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadKaTeX()
      .then(() => {
        setKatexReady(true);
      })
      .catch((err) => {
        setError(err.message);
      });
  }, []);

  useEffect(() => {
    if (katexReady && window.katex) {
      try {
        // Clean the formula string (remove surrounding $ or $$)
        let cleanFormula = formula.trim();
        if (cleanFormula.startsWith('$$') && cleanFormula.endsWith('$$')) {
          cleanFormula = cleanFormula.slice(2, -2);
        } else if (cleanFormula.startsWith('$') && cleanFormula.endsWith('$')) {
          cleanFormula = cleanFormula.slice(1, -1);
        }

        const renderedFormula = window.katex.renderToString(cleanFormula, {
          displayMode: displayMode,
          throwOnError: false,
          errorColor: '#cc0000',
          strict: false,
          trust: false,
          macros: {
            // Add common macros if needed
            "\\RR": "\\mathbb{R}",
            "\\ZZ": "\\mathbb{Z}",
            "\\QQ": "\\mathbb{Q}",
            "\\NN": "\\mathbb{N}",
            "\\CC": "\\mathbb{C}",
          }
        });
        
        setRendered(renderedFormula);
        setError(null);
      } catch (err) {
        console.warn('KaTeX rendering failed:', err);
        setError('Failed to render formula');
        setRendered(formula); // Fallback to original text
      }
    }
  }, [formula, displayMode, katexReady]);

  if (error) {
    return (
      <span className={`formula-error ${className}`}>
        <span className="formula-error-message">
          Formula Error: {error}
        </span>
        <span className={`formula-error-formula`}>
          {formula}
        </span>
      </span>
    );
  }

  if (!katexReady) {
    return (
      <span className={`formula-loading ${className}`}>
        <span className={`formula-loading-message`}>
          Loading formula...
        </span>
        <span className="formula-loading-formula">
          {formula}
        </span>
      </span>
    );
  }

  const modeClass = displayMode ? 'display-mode' : 'inline-mode';

  return (
    <span 
      className={`formula-renderer ${modeClass} ${className}`}
    >
      <span 
        dangerouslySetInnerHTML={{ __html: rendered || formula }}
      />
    </span>
  );
};

type Node = {
  type: string;
  value?: string;
  children?: Tree[];
  position?: { 
    start: { line: number; column: number; offset: number };
    end: { line: number; column: number; offset: number };
  };
  data?: unknown;
}

type Tree = Node & {};

// Remark plugin to parse LaTeX formulas
export const remarkLatex = () => {
  return (tree: Tree) => {
    const visit = (parent: Node | null, node: Node) => {
      if (node.type === 'text' && node.value) {
        const parts: Node[] = [];
        let currentIndex = 0;
        const text = node.value;
        
        const formulaRegex = /(\$\$[\s\S]*?\$\$|\$[^\$\n]*?\$)/g;
        let match;

        while ((match = formulaRegex.exec(text)) !== null) {
          // Add text before the formula
          if (match.index > currentIndex) {
            const textPart = text.slice(currentIndex, match.index);
            if (textPart) {
              parts.push({
                type: 'text',
                value: textPart
              });
            }
          }

          // Add the formula as a custom node
          const formulaContent = match[1];
          const isDisplayMode = formulaContent.startsWith('$$');
          parts.push({
            type: 'latexFormula',
            value: formulaContent,
            data: {
              hName: 'span',
              hProperties: {
                className: ['latex-formula'],
                'data-display-mode': isDisplayMode,
                'data-formula': encodeURIComponent(formulaContent)
              }
            }
          });

          currentIndex = match.index + match[1].length;
        }

        // Add remaining text
        if (currentIndex < text.length) {
          const remainingText = text.slice(currentIndex);
          if (remainingText) {
            parts.push({
              type: 'text',
              value: remainingText
            });
          }
        }

        // Replace the node with parsed parts if formulas were found
        if (parts.length > 0) {
          if (parent && parent.children) {
            const nodeIndex = parent.children.indexOf(node);
            parent.children.splice(nodeIndex, 1, ...parts);
          }
        }
      }

      if (node.children) {
        node.children.forEach(child => visit(node, child));
      }
    };

    visit(null, tree);
  };
};

// Declare global katex type
declare global {
  interface Window {
    katex: {
      renderToString: (tex: string, options?: any) => string;
    };
  }
}